import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LayoutTemplate, Search } from 'lucide-react';
import { cn } from 'lib/utils';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { PageHeader } from 'components/ui/page-header';
import EmptyState from 'components/ui/EmptyState';
import { Skeleton } from 'components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui';
import { useAuthContext } from 'modules/auth/context';
import { ORG_USE_CASES, type OrgUseCase } from 'modules/auth/onboarding';
import {
  RecommendedTemplateRow,
  TEMPLATE_LANGUAGES,
  templateLanguageFor,
  useRecommendedTemplates,
  useTemplates,
  type TemplateLanguage,
  type TemplateSource,
  type TemplateSummary,
} from 'modules/templates';
import { PERMISSIONS } from 'modules/auth/permissions';
import { TemplateCard } from './TemplateCard';
import { TemplatePreviewSheet } from './TemplatePreviewSheet';
import { UseTemplateDialog, type UseTemplateTarget } from './UseTemplateDialog';

const ALL = 'all';
const SEARCH_DEBOUNCE_MS = 300;
const PAGE_SIZE = 24;

const isUseCase = (v: string | null): v is OrgUseCase => !!v && (ORG_USE_CASES as readonly string[]).includes(v);
const isLanguage = (v: string | null): v is TemplateLanguage => !!v && (TEMPLATE_LANGUAGES as readonly string[]).includes(v);
const SOURCES: readonly TemplateSource[] = ['platform', 'org'];
const isSource = (v: string | null): v is TemplateSource => !!v && (SOURCES as readonly string[]).includes(v);

/** Gallery of published starter tests. Filters live in the URL so a filtered view can be shared. */
export function TemplatesPage() {
  const { t, i18n } = useTranslation();
  const { state } = useAuthContext();
  const [params, setParams] = useSearchParams();

  const useCase = isUseCase(params.get('useCase')) ? (params.get('useCase') as OrgUseCase) : undefined;
  const sourceParam = params.get('source');
  const source: TemplateSource | undefined = isSource(sourceParam) ? sourceParam : undefined;
  const uiLanguage = templateLanguageFor(i18n.language);
  // Default to the UI language; `lang=all` shows every language.
  const langParam = params.get('lang');
  const language: TemplateLanguage | undefined = langParam === ALL ? undefined : isLanguage(langParam) ? langParam : uiLanguage;

  const [searchInput, setSearchInput] = useState(params.get('q') ?? '');
  const [search, setSearch] = useState(searchInput.trim());
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const updateParam = (key: string, value: string | undefined) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true },
    );

  useEffect(() => {
    updateParam('q', search || undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync only when the debounced value settles
  }, [search]);

  const { data, isLoading, isError, refetch, hasNextPage, fetchNextPage, isFetchingNextPage } = useTemplates({
    useCase,
    language,
    source,
    search: search || undefined,
    limit: PAGE_SIZE,
  });
  const templates = data?.pages.flatMap((page) => page.data) ?? [];

  const features = state.entitlements?.features;
  const lockedFor = useMemo(
    () => (required: string[]) => required.filter((key) => !features?.[key]?.enabled),
    [features],
  );

  // `?preview=<id>` (from onboarding picks / the empty tests list) opens that template once.
  const [previewId, setPreviewId] = useState<string | null>(params.get('preview'));
  const [useTarget, setUseTarget] = useState<UseTemplateTarget | null>(null);
  useEffect(() => {
    if (params.get('preview')) updateParam('preview', undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- consume the deep link on mount only
  }, []);

  const unfiltered = !useCase && !search && source !== 'org';
  const recommended = useRecommendedTemplates({ language: language ?? uiLanguage, limit: 3 }, unfiltered);
  const showRecommended = unfiltered && Boolean(recommended.data?.basedOnProfile) && (recommended.data?.data.length ?? 0) > 0;

  const canDeleteTemplate = (template: TemplateSummary) =>
    template.createdBy === state.user?.id || Boolean(state.permissions?.includes(PERMISSIONS.TESTS_DELETE));

  const clearFilters = () => {
    setSearchInput('');
    setParams(new URLSearchParams(), { replace: true });
  };

  return (
    <div className="mx-auto w-full max-w-6xl space-y-5 px-4 py-6 sm:px-6">
      <PageHeader title={t('templates.title')} description={t('templates.subtitle')}>
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="relative min-w-0 flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
            <Input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t('templates.search_placeholder')}
              aria-label={t('templates.search_placeholder')}
              className="pl-9"
            />
          </div>
          <Select value={langParam === ALL ? ALL : language ?? ALL} onValueChange={(v) => updateParam('lang', v)}>
            <SelectTrigger className="w-[8.5rem] shrink-0 sm:w-44" aria-label={t('templates.language_label')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t('templates.all_languages')}</SelectItem>
              {TEMPLATE_LANGUAGES.map((lang) => (
                <SelectItem key={lang} value={lang}>
                  {t(`templates.lang_${lang}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </PageHeader>

      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:gap-4">
        {/* Source: a track with a raised selection, so it never reads as a second row of chips. */}
        <div
          className="flex w-full shrink-0 rounded-lg bg-muted p-1 sm:inline-flex sm:w-auto"
          role="group"
          aria-label={t('templates.source_label')}
        >
          {[undefined, ...SOURCES].map((key) => {
            const selected = source === key;
            return (
              <button
                key={key ?? ALL}
                type="button"
                aria-pressed={selected}
                onClick={() => updateParam('source', key)}
                className={cn(
                  'h-9 flex-1 whitespace-nowrap rounded-md px-3.5 text-sm transition-[background-color,color,box-shadow] focus-visible:!outline-none focus-visible:ring-2 focus-visible:ring-ring sm:flex-none',
                  selected ? 'bg-card font-semibold text-foreground shadow-xs' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {t(`templates.source_${key ?? ALL}`)}
              </button>
            );
          })}
        </div>

        {/* Use cases scroll sideways on phones; the right edge fades to show there is more. */}
        <div
          className="-mx-4 min-w-0 overflow-x-auto px-4 [mask-image:linear-gradient(to_right,black_calc(100%-2rem),transparent)] sm:mx-0 sm:px-0 sm:[mask-image:none] lg:flex-1"
          role="group"
          aria-label={t('auth.ob_step_usecase')}
        >
          <div className="flex w-max gap-2 pr-6 sm:w-auto sm:flex-wrap sm:pr-0">
            {[undefined, ...ORG_USE_CASES].map((key) => {
              const selected = useCase === key;
              return (
                <button
                  key={key ?? ALL}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => updateParam('useCase', key)}
                  className={cn(
                    'h-9 shrink-0 rounded-full border px-3.5 text-sm transition-colors focus-visible:!outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    selected
                      ? 'border-foreground bg-foreground font-medium text-background'
                      : 'border-border bg-card text-foreground/80 hover:border-foreground/30 hover:text-foreground',
                  )}
                >
                  {key ? t(`auth.ob_usecase_${key}`) : t('templates.all_use_cases')}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {showRecommended ? (
        <section aria-labelledby="recommended-templates" className="space-y-2.5">
          <h2 id="recommended-templates" className="text-sm font-semibold text-foreground">
            {t('templates.recommended_title')}
          </h2>
          <RecommendedTemplateRow templates={recommended.data?.data ?? []} onSelect={(tpl) => setPreviewId(tpl.id)} />
        </section>
      ) : null}

      {isError ? (
        <EmptyState
          icon={<LayoutTemplate />}
          title={t('templates.error_title')}
          secondaryAction={{ label: t('templates.retry'), onClick: () => refetch() }}
        />
      ) : isLoading ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" aria-busy="true">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} className="h-36 rounded-lg" />
          ))}
        </div>
      ) : templates.length === 0 ? (
        <EmptyState
          icon={<LayoutTemplate />}
          title={t('templates.empty_title')}
          description={t('templates.empty_desc')}
          secondaryAction={{ label: t('templates.clear_filters'), onClick: clearFilters }}
        />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {templates.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              lockedFeatures={lockedFor(template.requiredFeatures)}
              onOpen={() => setPreviewId(template.id)}
            />
          ))}
        </div>
      )}

      {hasNextPage && !isError ? (
        <div className="flex justify-center">
          <Button type="button" variant="outline" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
            {isFetchingNextPage ? t('loading') : t('templates.show_more')}
          </Button>
        </div>
      ) : null}

      <TemplatePreviewSheet
        templateId={previewId}
        canDelete={canDeleteTemplate}
        onClose={() => setPreviewId(null)}
        onUse={(id, title) => {
          setPreviewId(null);
          setUseTarget({ id, title, mode: 'copy' });
        }}
        onAdapt={(id, title) => {
          setPreviewId(null);
          setUseTarget({ id, title, mode: 'adapt' });
        }}
      />
      <UseTemplateDialog target={useTarget} onClose={() => setUseTarget(null)} />
    </div>
  );
}

export default TemplatesPage;
